import { useRef } from 'react';

export default function Features() {
  const scrollRef = useRef<HTMLDivElement>(null);

  const features = [
    {
      icon: 'ri-flashlight-line',
      title: 'Speed Optimized',
      description: 'Lightning-fast performance with minimal resource consumption for seamless user experience'
    },
    {
      icon: 'ri-shield-star-line',
      title: 'Military-Grade Security',
      description: 'Advanced encryption protocols protecting your data with industry-leading security standards'
    },
    {
      icon: 'ri-global-line',
      title: 'Global Coverage',
      description: 'Worldwide server network ensuring reliable connectivity from anywhere on the planet'
    },
    {
      icon: 'ri-battery-charge-line',
      title: 'Power Efficient',
      description: 'Optimized battery usage allowing extended operation without draining device resources'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Real-Time Analytics',
      description: 'Comprehensive monitoring and insights providing detailed performance metrics instantly'
    }
  ];

  return (
    <section id="features" className="bg-white py-32">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-16">
          <p className="text-xs uppercase tracking-widest text-[#6B5B95] mb-4">CAPABILITIES</p>
          <h2 className="text-5xl font-bold text-[#0A1628]">Built for Performance</h2>
        </div>

        <div className="relative">
          <div 
            ref={scrollRef}
            className="flex gap-8 overflow-x-auto pb-6 scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {features.map((feature, index) => (
              <div 
                key={index}
                className="flex-shrink-0 w-96 h-72 bg-[#FAFBFC] rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-5xl text-[#2D1B69]`}></i>
                </div>
                <h3 className="text-xl font-semibold text-[#0A1628] mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Technology Stack */}
        <div className="mt-32 bg-[#0D1B2A] rounded-3xl p-16">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest text-blue-400 mb-4">BUILT WITH</p>
            <h2 className="text-5xl font-bold text-white">Modern iOS Technologies</h2>
          </div>

          <div className="grid grid-cols-3 gap-20">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <i className="ri-code-box-line text-6xl bg-gradient-to-br from-blue-400 to-purple-500 bg-clip-text text-transparent"></i>
              </div>
              <h3 className="text-2xl font-semibold text-white mb-6">Development</h3>
              <ul className="space-y-3 text-white/70">
                <li>Swift & SwiftUI</li>
                <li>Xcode & TestFlight</li>
                <li>Core Data & CloudKit</li>
                <li>Combine Framework</li>
              </ul>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <i className="ri-lock-line text-6xl bg-gradient-to-br from-blue-400 to-purple-500 bg-clip-text text-transparent"></i>
              </div>
              <h3 className="text-2xl font-semibold text-white mb-6">Security</h3>
              <ul className="space-y-3 text-white/70">
                <li>AES-256 Encryption</li>
                <li>Keychain Services</li>
                <li>Secure Enclave</li>
                <li>App Transport Security</li>
              </ul>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <i className="ri-speed-line text-6xl bg-gradient-to-br from-blue-400 to-purple-500 bg-clip-text text-transparent"></i>
              </div>
              <h3 className="text-2xl font-semibold text-white mb-6">Performance</h3>
              <ul className="space-y-3 text-white/70">
                <li>Metal Graphics API</li>
                <li>Grand Central Dispatch</li>
                <li>Memory Management</li>
                <li>Network Optimization</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
